import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  Mic2, 
  Users, 
  Globe2, 
  Rocket, 
  ChevronRight, 
  Play,
  Quote
} from 'lucide-react';
import { useSettings } from '../hooks/useSettings';
import { toast } from 'react-hot-toast';

const Project = () => {
  const { settings, loading: settingsLoading } = useSettings();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  // Get image URLs from settings or use defaults
  const getImageUrl = (key: string, defaultUrl: string) => {
    if (!settings) return defaultUrl;
    const imageSettings = settings.media.imageUrls || {};
    return imageSettings[key] || defaultUrl;
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const features = [
    {
      icon: Mic2,
      title: "O que é?",
      description: "Este projeto documenta as atividades mais destacadas ao longo do ano letivo. Abrange educação, cultura, desporto, cidadania e muito mais."
    },
    {
      icon: Users,
      title: "Inclusão e Representatividade",
      description: "Damos voz a grupos vulneráveis, emigrantes e alunos do ensino especial, promovendo um espaço de reflexão, liberdade de pensamento e novas relações interpessoais."
    },
    {
      icon: Globe2,
      title: "Ligação com a Comunidade",
      description: "Valorizamos as boas práticas e fortalecemos laços entre alunos, professores e a sociedade."
    },
    {
      icon: Rocket,
      title: "Desenvolvendo Competências",
      description: "Além de destacar eventos, ajudamos a desenvolver comunicação, autonomia, colaboração e pensamento crítico."
    }
  ];

  const testimonials = [
    {
      quote: "O projeto Sinopse tem sido uma plataforma incrível para dar voz aos nossos alunos e documentar os momentos mais importantes da nossa escola. É uma iniciativa que une tecnologia, educação e criatividade.",
      author: "Hugo Carvalho",
      role: "Professor de Informática",
      image: `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/general/hugo_carvalho.jpg`
    },
    {
      quote: "Como coordenador do curso de multimédia, vejo o Sinopse como uma excelente oportunidade para os alunos desenvolverem competências técnicas e criativas. O projeto permite-lhes aplicar conhecimentos de produção audiovisual em contexto real.",
      author: "Paulo Calhau",
      role: "Professor de Multimédia",
      image: `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/general/paulo_calhau.jpg`
    },
    {
      quote: "O projeto Sinopse representa a excelência e inovação que caracterizam o AEAAV. É uma iniciativa que promove o desenvolvimento de competências digitais e comunicacionais, fundamentais para o futuro dos nossos alunos.",
      author: "Albérico Vieira",
      role: "Diretor do AEAAV",
      image: `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/general/alberico_vieira.jpg`
    }
  ];

  return (
    <div className="min-h-screen pt-16">
      {/* Hero Section */}
      <div className="relative h-[70vh] overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.6)), url("${getImageUrl('project_hero', 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80')}")`,
            backgroundAttachment: 'fixed'
          }}
        />
        <div className="relative h-full flex items-center justify-center text-center px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Projeto 
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-primary via-primary/80 to-primary/60">
                {" "}SINOPSE
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-8">
              Uma janela para as atividades mais marcantes da nossa escola!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="group flex items-center justify-center gap-2 px-8 py-3 bg-primary text-primary-foreground rounded-full hover:bg-primary/90 transition-colors">
                <Play className="w-5 h-5" />
                Ver Vídeo de Apresentação
                <motion.span
                  initial={{ x: 0 }}
                  whileHover={{ x: 5 }}
                  transition={{ type: "spring", stiffness: 400, damping: 10 }}
                >
                  <ChevronRight className="w-5 h-5" />
                </motion.span>
              </button>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Features Section */}
      <div ref={ref} className="py-20 bg-gradient-to-b from-background via-secondary/20 to-background">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
            className="grid grid-cols-1 md:grid-cols-2 gap-8"
          >
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: index * 0.2 }}
                className="bg-card p-8 rounded-lg hover:ring-1 hover:ring-primary/50 transition-all"
              >
                <feature.icon className="w-12 h-12 text-primary mb-4" />
                <h3 className="text-xl font-semibold mb-4">{feature.title}</h3>
                <p className="text-gray-400 leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Image Gallery */}
      <div className="py-20 bg-gradient-to-b from-background to-secondary/20">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl font-bold mb-12 text-center">Momentos Marcantes</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="relative h-64 rounded-lg overflow-hidden group">
              <img
                src={getImageUrl('momentos_1', 'https://wp.sinopseaeaav.pt/wp-content/uploads/2025/03/Imagem-WhatsApp-2025-03-13-as-19.48.01_077350e7.jpg')}
                alt="Atividade escolar"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </div>
            <div className="relative h-64 rounded-lg overflow-hidden group">
              <img
                src={getImageUrl('momentos_2', 'https://wp.sinopseaeaav.pt/wp-content/uploads/2025/03/Imagem-WhatsApp-2025-02-24-as-14.00.53_7373e59b.jpg')}
                alt="Atividade cultural"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </div>
            <div className="relative h-64 rounded-lg overflow-hidden group">
              <img
                src={getImageUrl('momentos_3', 'https://wp.sinopseaeaav.pt/wp-content/uploads/2025/03/Imagem-WhatsApp-2025-03-15-as-17.37.03_ce0a38f1.jpg')}
                alt="Atividade desportiva"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </div>
          </div>
        </div>
      </div>

      {/* Testimonials */}
      <div className="py-20 bg-gradient-to-b from-secondary/20 to-background">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl font-bold mb-12 text-center">O que dizem sobre nós</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                className="bg-card p-8 rounded-lg relative group hover:ring-1 hover:ring-primary/50 transition-all"
              >
                <Quote className="w-12 h-12 text-primary/20 absolute top-4 right-4" />
                <div className="w-16 h-16 rounded-full overflow-hidden mb-4 ring-4 ring-primary/20">
                  <img
                    src={testimonial.image}
                    alt={testimonial.author}
                    className="w-full h-full object-cover"
                  />
                </div>
                <p className="text-lg text-gray-300 mb-6 relative z-10">
                  "{testimonial.quote}"
                </p>
                <div>
                  <p className="font-semibold">{testimonial.author}</p>
                  <p className="text-sm text-primary">{testimonial.role}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Project;